// src/features/project/BacklogPanel.tsx
// Backlog list: ListItems + client-side filters, multi-select bulk-move to a sprint
// (BulkMoveItems), per-row sprint assign, add item. Row click opens the item rail.

import { useEffect, useMemo, useState } from 'react';
import { observer } from '@react';
import { useI18n, useProjectItem, useProjectSprint, useProjectEpic } from '@react/hooks';
import { Button, Input, Badge, ErrorAlert } from '@react/components';
import { Select } from '@/components/Select';
import { AddItemDialog } from './AddItemDialog';
import { idOf, listParams } from './meta';
import type { Entity } from '@core/types/entity';
import type { ProjectItem, Sprint, Epic } from '@core/types/project';

interface BacklogPanelProps {
  projectEntity: Entity;
  selectedItemId: string | null;
  onSelectItem: (item: ProjectItem, columns: never[]) => void;
  onItemsChanged: () => void;
  refreshToken: number;
}

export const BacklogPanel = observer(
  ({ projectEntity, selectedItemId, onSelectItem, onItemsChanged, refreshToken }: BacklogPanelProps) => {
    const { t } = useI18n();
    const itemStore = useProjectItem();
    const sprintStore = useProjectSprint();
    const epicStore = useProjectEpic();
    const projectId = projectEntity.id;

    const [sprints, setSprints] = useState<Sprint[]>([]);
    const [epics, setEpics] = useState<Epic[]>([]);
    const [search, setSearch] = useState('');
    const [sprintFilter, setSprintFilter] = useState('all');
    const [epicFilter, setEpicFilter] = useState('all');
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [bulkSprint, setBulkSprint] = useState('');
    const [showAdd, setShowAdd] = useState(false);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
      itemStore.ListItems(projectId, listParams());
      sprintStore.ListSprints(projectId, listParams()).then(() => setSprints([...sprintStore.items]));
      epicStore.ListEpics(projectId, listParams()).then(() => setEpics([...epicStore.items]));
    }, [projectId, refreshToken]);

    const items: ProjectItem[] = itemStore.items ?? [];

    const filtered = useMemo(() => {
      const q = search.trim().toLowerCase();
      return items.filter((it) => {
        if (sprintFilter === 'backlog' && it.sprint?.id) return false;
        if (sprintFilter !== 'all' && sprintFilter !== 'backlog' && it.sprint?.id !== sprintFilter) return false;
        if (epicFilter !== 'all' && it.epic?.id !== epicFilter) return false;
        if (q && !(it.title?.toLowerCase().includes(q) || it.item_key?.toLowerCase().includes(q))) return false;
        return true;
      });
    }, [items, search, sprintFilter, epicFilter]);

    const toggle = (id: string) => {
      setSelected((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    };

    const bulkMoveToSprint = async () => {
      if (!bulkSprint || selected.size === 0) return;
      setBusy(true);
      try {
        await itemStore.BulkMoveItems(projectId, {
          project: projectEntity,
          items: { entities: filtered.filter((i) => selected.has(idOf(i))).map((i) => i.entity!) },
          to_column_id: '',
          to_sprint: sprints.find((s) => idOf(s) === bulkSprint)?.entity,
        } as never);
        setSelected(new Set());
        setBulkSprint('');
        onItemsChanged();
      } catch {
        /* store surfaces error */
      } finally {
        setBusy(false);
      }
    };

    if (itemStore.isLoading && items.length === 0) {
      return <ListSkeleton />;
    }

    return (
      <div className="space-y-4">
        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="min-w-[180px] flex-1">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('project.backlog.search')}
            />
          </div>
          <div className="w-40">
            <Select value={sprintFilter} onChange={(e) => setSprintFilter(e.target.value)} aria-label={t('project.item.sprint')}>
              <option value="all">{t('project.backlog.allSprints')}</option>
              <option value="backlog">{t('project.backlog.onlyBacklog')}</option>
              {sprints.map((s) => (
                <option key={idOf(s)} value={idOf(s)}>
                  {s.name}
                </option>
              ))}
            </Select>
          </div>
          <div className="w-40">
            <Select value={epicFilter} onChange={(e) => setEpicFilter(e.target.value)} aria-label={t('project.item.epic')}>
              <option value="all">{t('project.backlog.allEpics')}</option>
              {epics.map((ep) => (
                <option key={idOf(ep)} value={idOf(ep)}>
                  {ep.name}
                </option>
              ))}
            </Select>
          </div>
          <Button size="sm" onClick={() => setShowAdd(true)}>
            {t('project.item.add')}
          </Button>
        </div>

        {!!itemStore.error && (
          <ErrorAlert error={itemStore.error} dismissible onDismiss={() => itemStore.clearError()} />
        )}

        {/* Bulk bar */}
        {selected.size > 0 && (
          <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2">
            <span className="text-sm text-foreground">
              {t('project.backlog.selectedCount').replace('{n}', String(selected.size))}
            </span>
            <div className="w-44">
              <Select value={bulkSprint} onChange={(e) => setBulkSprint(e.target.value)} aria-label={t('project.backlog.moveToSprint')}>
                <option value="">{t('project.backlog.moveToSprint')}</option>
                {sprints.map((s) => (
                  <option key={idOf(s)} value={idOf(s)}>
                    {s.name}
                  </option>
                ))}
              </Select>
            </div>
            <Button size="sm" variant="secondary" onClick={bulkMoveToSprint} disabled={busy || !bulkSprint}>
              {t('project.backlog.apply')}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())} disabled={busy}>
              {t('common.cancel')}
            </Button>
          </div>
        )}

        {/* List */}
        {filtered.length === 0 ? (
          <div className="py-16 text-center">
            <p className="text-lg font-medium text-foreground">{t('project.backlog.empty')}</p>
            <p className="mt-1 text-sm text-muted-foreground">{t('project.backlog.emptyDesc')}</p>
          </div>
        ) : (
          <ul className="divide-y divide-border rounded-lg border border-border">
            {filtered.map((item) => {
              const id = idOf(item);
              return (
                <li
                  key={id}
                  className={`flex items-center gap-3 px-3 py-2.5 ${id === selectedItemId ? 'bg-muted/50' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={selected.has(id)}
                    onChange={() => toggle(id)}
                    aria-label={t('project.backlog.selectItem')}
                    className="h-4 w-4 rounded border-input"
                  />
                  <button
                    type="button"
                    onClick={() => onSelectItem(item, [])}
                    className="flex min-w-0 flex-1 items-center gap-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    {item.item_key && (
                      <span className="font-mono text-xs text-muted-foreground">{item.item_key}</span>
                    )}
                    <span className="min-w-0 flex-1 truncate text-sm text-foreground">{item.title}</span>
                    {item.status && <Badge variant="secondary" size="sm">{item.status}</Badge>}
                    {item.sprint?.id && <Badge variant="primary" size="sm">{t('project.backlog.inSprint')}</Badge>}
                    {item.story_points != null && (
                      <span className="rounded-full bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                        {item.story_points}
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        <AddItemDialog
          open={showAdd}
          projectEntity={projectEntity}
          onClose={() => setShowAdd(false)}
          onAdded={onItemsChanged}
        />
      </div>
    );
  },
);

BacklogPanel.displayName = 'BacklogPanel';

function ListSkeleton() {
  return (
    <div className="space-y-1">
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 px-3 py-2.5">
          <div className="h-4 w-4 animate-pulse rounded bg-muted" />
          <div className="h-4 w-16 animate-pulse rounded bg-muted" />
          <div className="h-4 flex-1 animate-pulse rounded bg-muted" />
          <div className="h-5 w-16 animate-pulse rounded-full bg-muted" />
        </div>
      ))}
    </div>
  );
}
